// Iter 59 — Distributor Cheques Dashboard.
// Iter 62 — Replaced camera/manual entry with Master Calendar Scan
// auto-fill + interactive Pending/Paid status modal on row click.
// The 3-day Twilio WhatsApp alert cron continues to fire from the
// backend; this UI now also offers a one-tap "Trigger Alert Scan"
// secondary button for manual cron firing.
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CreditCard, Plus, AlertTriangle, Check, Trash2, Bell, Search, X,
  CircleDot, CheckCircle2,
} from "lucide-react";

function fmtAmount(n) { return `€${Number(n || 0).toFixed(2)}`; }
function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}
const EMPTY_FORM = {
  cheque_number: "", distributor_name: "", amount: "",
  due_date: "", bank: "", note: "",
};

export default function DistributorCheques() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [today, setToday] = useState("");
  const [busy, setBusy] = useState(false);
  const [alerting, setAlerting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Iter 62 — Master Calendar Scan state
  const [scanning, setScanning] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candIdx, setCandIdx] = useState(0);

  // Iter 62 — Pending/Paid status modal state
  const [modalRow, setModalRow] = useState(null);
  const [modalBusy, setModalBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/distributor-cheques?status=${filter}`);
      setRows(data.cheques || []);
      setToday(data.today || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const create = async () => {
    if (!form.cheque_number.trim() || !form.distributor_name.trim() || !form.amount || !form.due_date) {
      toast.error("Fill cheque #, distributor, amount, due date");
      return;
    }
    try {
      await api.post("/distributor-cheques", {
        ...form,
        amount: Number(form.amount),
      });
      toast.success("💾 Cheque saved to ledger");
      setForm(EMPTY_FORM);
      // If we just saved an auto-filled candidate, advance the queue.
      if (candidates.length > 0 && candIdx < candidates.length - 1) {
        const next = candIdx + 1;
        setCandIdx(next);
        applyCandidate(candidates[next]);
      } else {
        setCandidates([]);
        setCandIdx(0);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const applyCandidate = (c) => {
    setForm({
      cheque_number: c.cheque_number || "",
      distributor_name: c.distributor_name || "",
      amount: c.amount != null ? String(c.amount) : "",
      due_date: c.due_date || "",
      bank: "",
      note: c.location ? `Auto-filled from Master Calendar (${c.location})` : "Auto-filled from Master Calendar",
    });
  };

  // Iter 62 · scan the Master Cheque Calendar and auto-fill the form.
  const scanCalendar = async () => {
    setScanning(true);
    try {
      const { data } = await api.get("/distributor-cheques/calendar-candidates");
      const list = data.candidates || [];
      if (list.length === 0) {
        toast.info("📅 Master Calendar empty · no pending cheques to import", { duration: 4000 });
        setCandidates([]);
        setCandIdx(0);
        return;
      }
      setCandidates(list);
      setCandIdx(0);
      applyCandidate(list[0]);
      toast.success(`📅 Found ${list.length} calendar cheque(s) · #1 auto-filled · tap Save then Scan again for next`, { duration: 5000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Calendar scan failed");
    } finally { setScanning(false); }
  };

  const triggerAlertCron = async () => {
    setAlerting(true);
    try {
      const { data } = await api.post("/distributor-cheques/scan");
      if (data.reason === "twilio_not_configured") {
        toast.error("Twilio not configured · paste keys in Settings to enable alerts");
      } else {
        toast.success(`🔔 Alert scan · ${data.fired} WhatsApp alert(s) fired for ${data.target_date}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Alert scan failed"); }
    finally { setAlerting(false); }
  };

  // Iter 62 · Pending/Paid status modal actions
  const markPaid = async () => {
    if (!modalRow) return;
    setModalBusy(true);
    try {
      await api.patch(`/distributor-cheques/${modalRow.id}`, { cleared: true });
      toast.success(`🟢 Cheque #${modalRow.cheque_number} archived · removed from active monitor`);
      setModalRow(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setModalBusy(false); }
  };
  const keepPending = async () => {
    if (!modalRow) return;
    setModalBusy(true);
    try {
      // If row was previously cleared, un-clear it. Otherwise this is a no-op
      // (already pending) — we still re-fire to give the user feedback.
      if (modalRow.cleared) {
        await api.patch(`/distributor-cheques/${modalRow.id}`, { cleared: false });
        load();
      }
      toast.success(`🟡 Cheque #${modalRow.cheque_number} kept Pending · 3-day Twilio alert active`);
      setModalRow(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setModalBusy(false); }
  };
  const remove = async () => {
    if (!modalRow) return;
    if (!window.confirm("Delete this cheque entry permanently?")) return;
    setModalBusy(true);
    try {
      await api.delete(`/distributor-cheques/${modalRow.id}`);
      toast.success("🗑️ Cheque deleted");
      setModalRow(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setModalBusy(false); }
  };

  const stats = useMemo(() => {
    const upc = rows.filter((r) => !r.cleared && r.due_date >= today);
    const ovd = rows.filter((r) => !r.cleared && r.due_date < today);
    const sumUpc = upc.reduce((a, r) => a + Number(r.amount || 0), 0);
    const sumOvd = ovd.reduce((a, r) => a + Number(r.amount || 0), 0);
    return { upcCount: upc.length, ovdCount: ovd.length, sumUpc, sumOvd };
  }, [rows, today]);

  return (
    <div className="space-y-6" data-testid="cheques-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">💳 Επιταγές</h1>
          <p className="text-slate-500 mt-1">Master Calendar Scan auto-fills cheques · 3-day WhatsApp alerts fire automatically</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={scanCalendar}
            disabled={scanning}
            data-testid="cheques-scan-calendar-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded shadow flex items-center gap-2 disabled:opacity-50"
          >
            <Search className={`h-4 w-4 ${scanning ? "animate-pulse" : ""}`} />
            {scanning ? "Scanning…" : "🔍 Scan / Αναζήτηση"}
          </button>
          <button
            onClick={triggerAlertCron}
            disabled={alerting}
            data-testid="cheques-alert-scan-btn"
            className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-4 py-2 rounded shadow flex items-center gap-2 disabled:opacity-50"
          >
            <Bell className={`h-4 w-4 ${alerting ? "animate-pulse" : ""}`} />
            {alerting ? "Alerting…" : "🔔 Alert Scan"}
          </button>
        </div>
      </header>

      {/* Totals tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cheques-totals">
        <div className="bg-white border-2 border-slate-200 rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Upcoming</p>
          <p className="text-3xl font-black text-slate-900 tabular-nums mt-1">{stats.upcCount}</p>
        </div>
        <div className="bg-white border-2 border-amber-300 rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Σ Upcoming €</p>
          <p className="text-3xl font-black text-amber-700 tabular-nums mt-1">{fmtAmount(stats.sumUpc)}</p>
        </div>
        <div className="bg-white border-2 border-red-300 rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-red-700 font-bold">Overdue</p>
          <p className="text-3xl font-black text-red-700 tabular-nums mt-1">{stats.ovdCount}</p>
        </div>
        <div className="bg-white border-2 border-red-300 rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-red-700 font-bold">Σ Overdue €</p>
          <p className="text-3xl font-black text-red-700 tabular-nums mt-1">{fmtAmount(stats.sumOvd)}</p>
        </div>
      </div>

      {/* Auto-fill queue chip */}
      {candidates.length > 0 && (
        <div
          data-testid="cheques-queue-chip"
          className="bg-blue-50 border-2 border-blue-300 rounded-lg px-4 py-2 flex items-center justify-between text-sm"
        >
          <span className="text-blue-900 font-bold">
            📅 Master Calendar queue · {candIdx + 1} of {candidates.length} auto-filled
          </span>
          <button
            onClick={() => { setCandidates([]); setCandIdx(0); setForm(EMPTY_FORM); }}
            data-testid="cheques-queue-clear"
            className="text-blue-700 hover:text-blue-900 text-xs font-bold underline"
          >
            Clear queue
          </button>
        </div>
      )}

      {/* Create form */}
      <section className="bg-white border-2 border-slate-200 rounded-xl p-4 shadow-sm">
        <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
          <Plus className="h-5 w-5 text-blue-600" /> Save Cheque
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-2 items-end">
          {[
            ["cheque_number", "Cheque #", "text", "10247"],
            ["distributor_name", "Distributor", "text", "Laiko"],
            ["amount", "Amount €", "number", "850.00"],
            ["due_date", "Due Date", "date", ""],
            ["bank", "Bank", "text", "Bank of Cyprus"],
            ["note", "Note", "text", "Tobacco order"],
          ].map(([k, lbl, type, ph]) => (
            <div key={k} className={k === "note" ? "sm:col-span-2 lg:col-span-1" : ""}>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{lbl}</label>
              <input
                type={type} placeholder={ph}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                data-testid={`cheques-form-${k}`}
                className="w-full mt-1 border-2 border-slate-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <button
            onClick={create}
            data-testid="cheques-create-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-2 rounded text-sm flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> 💾 Save Cheque
          </button>
        </div>
      </section>

      {/* Filter + Table */}
      <section className="bg-white border-2 border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2 flex-wrap">
          <CreditCard className="h-5 w-5 text-slate-700" />
          <h2 className="font-bold text-slate-900">Cheque Ledger · Tap a row to change status</h2>
          <div className="ml-auto flex gap-1" data-testid="cheques-filter">
            {["all", "upcoming", "overdue", "cleared"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`cheques-filter-${f}`}
                className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-widest ${
                  filter === f ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700"
                }`}
              >{f}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cheques-table">
            <thead>
              <tr className="text-left border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-2">Due</th>
                <th className="px-4 py-2">Cheque #</th>
                <th className="px-4 py-2">Distributor</th>
                <th className="px-4 py-2">Bank</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2 text-right">Δ days</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const d = daysUntil(r.due_date);
                const overdue = !r.cleared && d != null && d < 0;
                const alertBand = !r.cleared && d != null && d >= 0 && d <= 3;
                return (
                  <tr
                    key={r.id}
                    onClick={() => setModalRow(r)}
                    data-testid={`cheques-row-${r.id}`}
                    className={`border-b border-slate-100 last:border-0 cursor-pointer ${
                      overdue ? "bg-red-50/60 hover:bg-red-100/60" : alertBand ? "bg-amber-50/60 hover:bg-amber-100/60" : "hover:bg-slate-50"
                    }`}
                  >
                    <td className="px-4 py-2 font-mono">{r.due_date}</td>
                    <td className="px-4 py-2 font-bold">{r.cheque_number}</td>
                    <td className="px-4 py-2">{r.distributor_name}</td>
                    <td className="px-4 py-2 text-slate-500">{r.bank || "—"}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-bold text-blue-700">{fmtAmount(r.amount)}</td>
                    <td className={`px-4 py-2 text-right tabular-nums font-bold ${
                      overdue ? "text-red-700" : alertBand ? "text-amber-700" : "text-slate-600"
                    }`}>
                      {d == null ? "—" : (d === 0 ? "TODAY" : (d > 0 ? `+${d}d` : `${d}d`))}
                      {alertBand && <AlertTriangle className="inline h-3 w-3 ml-1" />}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {r.cleared
                        ? <span className="text-emerald-700 font-bold inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> 🟢 Paid</span>
                        : overdue
                        ? <span className="text-red-700 font-bold">OVERDUE</span>
                        : <span className="text-amber-700 font-bold inline-flex items-center gap-1"><CircleDot className="h-3.5 w-3.5" /> 🟡 Pending</span>}
                      {r.alert_sent_at && <div className="text-[10px] text-amber-600 mt-0.5">📤 alert sent</div>}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">{busy ? "Loading…" : "No cheques yet · Scan Master Calendar or log manually above."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Iter 62 · Pending / Paid status modal */}
      {modalRow && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4"
          onClick={() => !modalBusy && setModalRow(null)}
          data-testid="cheques-status-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-black text-slate-900">Change Status</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Cheque <span className="font-mono font-bold">#{modalRow.cheque_number}</span> · {modalRow.distributor_name} · <span className="font-bold text-blue-700">{fmtAmount(modalRow.amount)}</span>
                </p>
              </div>
              <button
                onClick={() => setModalRow(null)}
                disabled={modalBusy}
                data-testid="cheques-modal-close"
                className="text-slate-400 hover:text-slate-700 p-1 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="space-y-2">
              <button
                onClick={keepPending}
                disabled={modalBusy}
                data-testid="cheques-modal-pending"
                className="w-full bg-amber-100 hover:bg-amber-200 border-2 border-amber-400 text-amber-900 font-bold p-4 rounded-xl text-left flex items-center gap-3 disabled:opacity-50"
              >
                <CircleDot className="h-6 w-6 text-amber-600" />
                <div>
                  <div className="text-base">🟡 Pending (Εκκρεμεί)</div>
                  <div className="text-[11px] font-normal text-amber-700 mt-0.5">Keep in active monitor · 3-day Twilio WhatsApp alert stays armed</div>
                </div>
              </button>

              <button
                onClick={markPaid}
                disabled={modalBusy}
                data-testid="cheques-modal-paid"
                className="w-full bg-emerald-100 hover:bg-emerald-200 border-2 border-emerald-400 text-emerald-900 font-bold p-4 rounded-xl text-left flex items-center gap-3 disabled:opacity-50"
              >
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                <div>
                  <div className="text-base">🟢 Paid (Πληρώθηκε)</div>
                  <div className="text-[11px] font-normal text-emerald-700 mt-0.5">Archive immediately · removes from active calendar monitor</div>
                </div>
              </button>

              <button
                onClick={remove}
                disabled={modalBusy}
                data-testid="cheques-modal-delete"
                className="w-full text-red-700 hover:bg-red-50 border-2 border-red-200 font-bold p-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" /> Delete entry permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
